import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { Box, Container, Tab, Tabs, Alert, ToggleButtonGroup, ToggleButton } from '@mui/material';
import { UserRole } from '@features/authentication/types';
import { useInsightContext } from '../contexts/InsightContext';
import { DateRangeFilter } from '../components/DateRangeFilter';
import TenantSelector from '../components/TenantSelector';

/**
 * Tab definition for insight navigation
 */
interface InsightTab {
  label: string;
  path: string;
}

/**
 * Tab paths hidden per role.
 * Roles not listed here see all tabs.
 */
export const HIDDEN_TAB_PATHS: Partial<Record<UserRole, ReadonlyArray<string>>> = {
  [UserRole.FACULTY_ADMIN]: ['institutes'],
  [UserRole.PROFESSOR]: ['institutes', 'subjects'],
};

/**
 * Insight layout with tab navigation and global filters
 *
 * Provides the shared header, tenant selector (for SYSTEM_ADMIN),
 * date range filter, and tab navigation.
 * Renders child tab content via <Outlet />.
 */
const InsightLayout: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    selectedTenantId,
    setSelectedTenantId,
    queryEnabled,
    activeRole,
    dateRangePreset,
    setDateRangePreset,
    customDateRange,
    setCustomDateRange,
    granularity,
    setGranularity,
  } = useInsightContext();

  const allTabs: InsightTab[] = useMemo(
    () => [
      { label: t('insight.tabs.overview', 'Overview'), path: '' },
      {
        label: t('insight.tabs.institutes', 'Institutes'),
        path: 'institutes',
      },
      {
        label: t('insight.tabs.subjects', 'Subjects'),
        path: 'subjects',
      },
      {
        label: t('insight.tabs.surveys', 'Surveys'),
        path: 'surveys',
      },
      {
        label: t('insight.tabs.surveyInsight', 'Survey Insight'),
        path: 'question-insights',
      },
    ],
    [t]
  );

  // Filter tabs based on current role
  const hiddenPaths = useMemo(
    () => (activeRole.currentRole ? (HIDDEN_TAB_PATHS[activeRole.currentRole] ?? []) : []),
    [activeRole.currentRole]
  );

  const tabs = useMemo(
    () => allTabs.filter((tab) => !hiddenPaths.includes(tab.path)),
    [allTabs, hiddenPaths]
  );

  // Redirect away from hidden tab URLs (e.g. direct navigation or bookmark)
  useEffect(() => {
    const isOnHiddenTab = hiddenPaths.some((p) => p && location.pathname.endsWith(p));
    if (isOnHiddenTab) {
      navigate('.', { replace: true });
    }
  }, [hiddenPaths, location.pathname, navigate]);

  // Determine active tab from current path
  const activeTab = useMemo(() => {
    const currentPath = location.pathname;
    // Check if path ends with a tab path
    for (let i = tabs.length - 1; i >= 0; i--) {
      if (tabs[i].path && currentPath.endsWith(tabs[i].path)) {
        return i;
      }
    }
    return 0; // Default to overview
  }, [location.pathname, tabs]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    const tab = tabs[newValue];
    if (tab.path) {
      navigate(tab.path);
    } else {
      // Navigate to the base insight path (overview / index route)
      navigate('.');
    }
  };

  return (
    <Container maxWidth={false} disableGutters>
      {/* Tab Navigation + Filters */}
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'stretch', lg: 'center' },
          flexWrap: 'wrap',
          borderBottom: 1,
          borderColor: 'divider',
          mb: 1.5,
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ flexShrink: { xs: 1, lg: 0 }, minWidth: 0 }}
        >
          {tabs.map((tab) => (
            <Tab key={tab.path} label={tab.label} />
          ))}
        </Tabs>

        <Box
          sx={{
            ml: { xs: 0, lg: 'auto' },
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1.5,
            py: { xs: 1, lg: 0 },
          }}
        >
          {queryEnabled && (
            <>
              <DateRangeFilter
                value={dateRangePreset}
                onChange={(preset, dateRange) => {
                  setDateRangePreset(preset);
                  if (preset === 'custom' && dateRange) {
                    setCustomDateRange(dateRange);
                  }
                }}
                customDateRange={customDateRange}
                onCustomDateRangeChange={setCustomDateRange}
              />

              <ToggleButtonGroup
                value={granularity}
                exclusive
                onChange={(_, newValue) => {
                  if (newValue !== null) {
                    setGranularity(newValue);
                  }
                }}
                size="small"
              >
                <ToggleButton value="day">{t('insight.filters.day')}</ToggleButton>
                <ToggleButton value="week">{t('insight.filters.week')}</ToggleButton>
                <ToggleButton value="month">{t('insight.filters.month')}</ToggleButton>
              </ToggleButtonGroup>
            </>
          )}

          {activeRole.currentRole === UserRole.SYSTEM_ADMIN && (
            <Box sx={{ width: { xs: 240, lg: 280 }, flexShrink: 0 }}>
              <TenantSelector
                selectedTenantId={selectedTenantId}
                onTenantSelect={setSelectedTenantId}
              />
            </Box>
          )}
        </Box>
      </Box>

      {/* Query disabled state */}
      {!queryEnabled ? (
        <Alert severity="info">
          {activeRole.currentRole === UserRole.SYSTEM_ADMIN
            ? t('insight.tenantSelector.selectPrompt')
            : t('insight.tenantSelector.contextError')}
        </Alert>
      ) : (
        /* Tab Content */
        <Outlet />
      )}
    </Container>
  );
};

export default InsightLayout;
